import { createRoute, lazyRouteComponent } from "@tanstack/react-router";
import { z } from "zod";
import { useCheckoutStore } from "../stores";
import { SEARCH_PAGE_VARIANTS } from "../utils/posthog";
import { localeRoute } from "./locale";

const searchParamsSchema = z.object({
  from_path: z.string().optional(),
  trip_id: z.string(),
  session_id: z.string(),
  ref: z.string().optional(),
  utm_campaign: z.string().optional(),
  branch: z.string().optional(),
  mode: z.string().optional(),
  num_passengers: z.number().or(z.string()).optional(),
  currency: z.string().optional(),
  start_address: z.string().optional(),
  start_name: z.string().optional(),
  flight_datetime: z.string().optional(),
  pickup_datetime: z.string().optional(),
  end_address: z.string().optional(),
  end_name: z.string().optional(),
  return_flight_datetime: z.string().optional(),
  return_pickup_datetime: z.string().optional(),
  airline: z.string().optional(),
  flight_number: z.number().or(z.string()).optional(),
  airline2: z.string().optional(),
  flight_number2: z.number().or(z.string()).optional(),
  dial_code: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().optional(),
  phone_number: z.number().or(z.string()).optional(),
  coupon_code: z.number().or(z.string()).optional(),
  partner_tracking_id: z.number().or(z.string()).optional(),
  gid: z.number().or(z.string()).optional(),
  guid: z.number().or(z.string()).optional(),
  customer_special_instructions: z.string().optional(),
  search_id: z.string().optional(),
  result_id: z.string().optional(),
  selectedTicketId: z.string().optional(),
  selectedScheduleIndex: z.number().or(z.string()).optional(),
  returnSelectedTicketId: z.string().optional(),
  returnSelectedScheduleIndex: z.number().or(z.string()).optional(),
  // A bad value must not fail the whole parse: that throws and blocks checkout.
  srp_variant: z.enum(SEARCH_PAGE_VARIANTS).optional().catch(undefined),
});

export type BookSearchParams = z.infer<typeof searchParamsSchema>;

export const bookRoute = createRoute({
  validateSearch: (search: Record<string, unknown>): BookSearchParams => {
    const result = searchParamsSchema.safeParse(search);

    if (result.success) {
      const params = {
        ...result.data,
        partner_tracking_id:
          result.data.partner_tracking_id ||
          result.data.guid ||
          result.data.gid,
        search_id: result.data.session_id || result.data.search_id,
        result_id: result.data.trip_id || result.data.result_id,
      };

      useCheckoutStore.getState().handleAddSearchParams(params);
      return params;
    }

    console.error("Invalid search params:", result.error?.errors);
    throw new Error("Invalid query parameters");
  },

  getParentRoute: () => localeRoute,
  path: "/book",
  component: lazyRouteComponent(() => import("../pages/checkout-page")),
});
